// ---------------------------------------------------------------------------
// useAppOntology — React hook that builds a memoized AppOntology from the
// current editor store data (blocks, crossRef, entityIndex).
// ---------------------------------------------------------------------------

import { useMemo } from 'react'
import {
  buildAppOntology,
  type AppOntology,
} from '@/features/flow-editor/services/app-ontology'
import { useCrossReferenceIndex } from '@/lib/cross-reference'
import { useEntityIndex } from '@/features/data-explorer/hooks/use-entity-index'
import { useEditorStore } from '@/features/page-editor/store/editor-store'

// Stable empty references to avoid new object/array on every render when app is null
const EMPTY_BLOCKS: Record<string, unknown> = {}
const EMPTY_FLOWS: Record<string, unknown> = {}

/**
 * Merge top-level EditorStore.flows (namespaced as "blockName.flowName")
 * back into block objects so buildAppOntology() can find them.
 *
 * EditorStore.blocks (CogoBlockDef) has no .flow field -- flows are stored
 * separately in EditorStore.flows. This function creates enriched block
 * objects with .flow populated for ontology consumption.
 *
 * Per STUDIO-UX-03: fixes Data Flow canvas showing zero edges.
 */
export function mergeFlowsIntoBlocks(
  blocks: Record<string, unknown>,
  flows: Record<string, unknown>,
): Record<string, any> {
  if (!flows || Object.keys(flows).length === 0) return blocks as any

  const merged: Record<string, any> = {}

  for (const [blockName, block] of Object.entries(blocks)) {
    const blockFlows: Record<string, unknown> = {}

    // Find flows namespaced to this block: "blockName.flowName"
    for (const [flowKey, flow] of Object.entries(flows)) {
      if (flowKey.startsWith(`${blockName}.`)) {
        const localName = flowKey.slice(blockName.length + 1)
        blockFlows[localName] = flow
      }
    }

    // If no namespaced flows found, try assigning ALL bare-name flows
    // (for single-block templates where flows aren't namespaced)
    if (Object.keys(blockFlows).length === 0 && Object.keys(blocks).length === 1) {
      for (const [flowKey, flow] of Object.entries(flows)) {
        if (!flowKey.includes('.')) {
          blockFlows[flowKey] = flow
        }
      }
    }

    merged[blockName] = {
      ...(block as object),
      flow: Object.keys(blockFlows).length > 0 ? blockFlows : (block as any).flow,
    }
  }

  return merged
}

/** Build and return a memoized AppOntology. */
export function useAppOntology(): AppOntology {
  const blocks = useEditorStore((s) => s.blocks ?? EMPTY_BLOCKS)
  const appFlows = useEditorStore((s) => s.flows ?? EMPTY_FLOWS)
  const crossRef = useCrossReferenceIndex()
  const entityIndex = useEntityIndex()

  return useMemo(() => {
    // Merge top-level flows into blocks for ontology (STUDIO-UX-03)
    const mergedBlocks = mergeFlowsIntoBlocks(blocks, appFlows)
    return buildAppOntology({ blocks: mergedBlocks, crossRef, entityIndex })
  }, [blocks, appFlows, crossRef, entityIndex])
}
